import React, { useEffect, useMemo, useState } from "react";
import {
  DateTimeDisplay,
  Breadcrumbs,
  SearchQuery,
  Pagination,
} from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { FilterBtn } from "../../components/ui/Buttons";
import SortIcon from "../../components/ui/Sorting";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";

import {
  listUserActivity,
  listUserActivityStatistics,
} from "../../services/admin/userActivity/activity.service";
import { UserActivitySummary } from "../../services/admin/userActivity/activity.model";
import UserActivityLogFilter from "../../components/filters/UserActivityLogFilter";

type ActivitySortColumn = "name" | "email" | "activityType" | "timestamp";

type ActivityRow = {
  id: string;
  userName: string;
  email: string;
  activityType: string;
  details: string;
  timestamp: string;
};

const SUMMARY_SKELETON_KEYS = [
  "total",
  "login",
  "failed",
  "logout",
  "unique",
] as const;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const toText = (value: unknown, fallback = "") => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }

  return fallback;
};

const normalizeActivityRow = (item: Record<string, unknown>): ActivityRow => {
  const email = toText(item.email, "unknown");
  const activityType = toText(item.activityType, "activity").toLowerCase();
  const timestamp = toText(item.timestamp ?? item.time, "");
  const id = toText(item.id, "") || timestamp || `${email}-${activityType}`;

  return {
    userName: toText(item.userName ?? item.name, ""),
    email: toText(item.email, ""),
    details: toText(item.details ?? item.description, ""),
    id,
    activityType,
    timestamp,
  };
};

const getActivityTypeOptions = (rows: ActivityRow[]) => {
  const types = Array.from(new Set(rows.map(r => r.activityType).filter(Boolean)));

  return [
    { label: "All Activities", value: "all" },
    ...types.map(type => ({
      label: type.charAt(0).toUpperCase() + type.slice(1),
      value: type,
    })),
  ];
};

const getHeaderTitle = (statusFilter: string) => {
  if (statusFilter === "all") {
    return "All Activities";
  }

  return `${statusFilter.charAt(0).toUpperCase()}${statusFilter.slice(1)} Activities`;
};

const getSortIconOrder = (
  activeColumn: ActivitySortColumn,
  column: ActivitySortColumn,
  order: "asc" | "desc"
) => {
  if (activeColumn !== column) {
    return null;
  }

  return order;
};

const compareRows = (
  a: ActivityRow,
  b: ActivityRow,
  sortColumn: ActivitySortColumn,
  sortOrder: "asc" | "desc"
) => {
  const multiplier = sortOrder === "asc" ? 1 : -1;

  switch (sortColumn) {
    case "name":
      return (a.userName ?? "").localeCompare(b.userName ?? "") * multiplier;
    case "email":
      return (a.email ?? "").localeCompare(b.email ?? "") * multiplier;
    case "activityType":
      return (a.activityType ?? "").localeCompare(b.activityType ?? "") * multiplier;
    case "timestamp":
    default:
      return (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) * multiplier;
  }
};

type ActivityContentProps = {
  loading: boolean;
  error: string | null;
  pagedRows: ActivityRow[];
  sortColumn: ActivitySortColumn;
  sortOrder: "asc" | "desc";
  handleSort: (column: ActivitySortColumn) => void;
  searchQuery: string;
  onRetry: () => void;
};

const ActivityContent: React.FC<ActivityContentProps> = ({
  loading,
  error,
  pagedRows,
  sortColumn,
  sortOrder,
  handleSort,
  searchQuery,
  onRetry,
}) => {
  if (loading) {
    return <LoadingSpinner message="Loading activity logs..." />;
  }

  if (error) {
    return (
      <div>
        <div style={{ color: "red" }}>
          Error loading activity: {error}
        </div>
        <br />
        <button className="btn btn--secondary" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <table className="table-responsive">
      <thead>
        <tr>
          <th onClick={() => handleSort("name")}>
            Name <SortIcon sortOrder={getSortIconOrder(sortColumn, "name", sortOrder)} />
          </th>
          <th onClick={() => handleSort("email")}>
            Email <SortIcon sortOrder={getSortIconOrder(sortColumn, "email", sortOrder)} />
          </th>
          <th onClick={() => handleSort("activityType")}>
            Activity <SortIcon sortOrder={getSortIconOrder(sortColumn, "activityType", sortOrder)} />
          </th>
          <th>Description</th>
          <th onClick={() => handleSort("timestamp")}>
            Time <SortIcon sortOrder={getSortIconOrder(sortColumn, "timestamp", sortOrder)} />
          </th>
        </tr>
      </thead>
      <tbody>
        {pagedRows.length > 0 ? (
          pagedRows.map(r => (
            <tr key={r.id}>
              <td>{r.userName}</td>
              <td>{r.email}</td>
              <td>{r.activityType}</td>
              <td>{r.details}</td>
              <td>{new Date(r.timestamp).toLocaleString()}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={5}>
              {searchQuery ? "No activity matches your search." : "No activity found."}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

type SummarySectionProps = {
  summaryLoading: boolean;
  summaryError: string | null;
  summary: UserActivitySummary | null;
  monthLabel: string;
  onRetry: () => void;
};

const SummarySection: React.FC<SummarySectionProps> = ({
  summaryLoading,
  summaryError,
  summary,
  monthLabel,
  onRetry,
}) => (
  <>
    {summaryLoading &&
      SUMMARY_SKELETON_KEYS.map(key => (
        <div key={key} className="card">
          <div className="card__content">
            <LoadingSpinner message="Loading..." />
          </div>
        </div>
      ))}

    {summaryError && (
      <div>
        <div style={{ color: "red" }}>{summaryError}</div>
        <br />
        <button className="btn btn--secondary" onClick={onRetry}>
          Retry
        </button>
      </div>
    )}

    {!summaryLoading && summary && (
      <>
        <div className="card card__active-users">
          <div className="card__icon--alarm"></div>
          <div className="card__content">
            <h6>{monthLabel}</h6>
            <h2>{summary.totalActivities.toLocaleString()}</h2>
            <span>Total Activities</span>
          </div>
        </div>
        <div className="card card__login-users">
          <div className="card__icon--alarm"></div>
          <div className="card__content">
            <h6>{monthLabel}</h6>
            <h2>{summary.loginCount.toLocaleString()}</h2>
            <span>Logins</span>
          </div>
        </div>
        <div className="card card__failed-users">
          <div className="card__icon--alarm"></div>
          <div className="card__content">
            <h6>{monthLabel}</h6>
            <h2>{summary.failedAttempts.toLocaleString()}</h2>
            <span>Failed Attempts</span>
          </div>
        </div>
        <div className="card card__logout-users">
          <div className="card__icon--alarm"></div>
          <div className="card__content">
            <h6>{monthLabel}</h6>
            <h2>{summary.logoutCount.toLocaleString()}</h2>
            <span>Logouts</span>
          </div>
        </div>
        <div className="card card__unique-users">
          <div className="card__icon--alarm"></div>
          <div className="card__content">
            <h6>{monthLabel}</h6>
            <h2>{summary.uniqueUsers.toLocaleString()}</h2>
            <span>Unique Users</span>
          </div>
        </div>
      </>
    )}
  </>
);

const AdminUserActivity: React.FC = () => {
  const breadcrumbs = useBreadcrumbs({
    currentPageTitle: "Admin - User Activity",
  });

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [sortColumn, setSortColumn] = useState<ActivitySortColumn>("timestamp");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 50;

  const loadActivity = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await listUserActivity();
      if (res?.success && res.data) {
        setRows(res.data.items.map(item => normalizeActivityRow(item as unknown as Record<string, unknown>)));
      } else {
        setError(res?.error?.message || "Failed to load activity logs");
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e, "Failed to load activity logs"));
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    setSummaryError(null);

    try {
      const res = await listUserActivityStatistics();
      if (res?.success && res.data) {
        setSummary(res.data);
      } else {
        setSummary(null);
        setSummaryError("Failed to load activity summary");
      }
    } catch (e: unknown) {
      setSummaryError(getErrorMessage(e, "Failed to load activity summary"));
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    void loadActivity();
    void loadSummary();
  }, []);

  const activityTypeOptions = useMemo(() => getActivityTypeOptions(rows), [rows]);

  const handleSort = (column: ActivitySortColumn) => {
    setSortOrder(prev => (sortColumn === column && prev === "asc" ? "desc" : "asc"));
    setSortColumn(column);
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...rows];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        [r.userName, r.email, r.activityType, r.details].some(
          field => field && String(field).toLowerCase().includes(q)
        )
      );
    }

    if (statusFilter !== "all") {
      result = result.filter(r => r.activityType === statusFilter);
    }

    result.sort((a, b) => compareRows(a, b, sortColumn, sortOrder));

    return result;
  }, [rows, searchQuery, statusFilter, sortColumn, sortOrder]);

  const totalPages = Math.max(1, Math.ceil((filteredAndSorted.length ?? 0) / pageSize));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSorted.slice(start, start + pageSize);
  }, [filteredAndSorted, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const headerTitle = getHeaderTitle(statusFilter);
  const monthLabel = new Date().toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <main>
      <div className="container">
        <section className="page">
          <h2 className="heading__title heading__title--activity">
            Admin - User Activity
          </h2>
          <DateTimeDisplay />
          <Breadcrumbs items={breadcrumbs} />
        </section>

        <section className="summary-grid-admin-activity mb-2">
          <SummarySection
            summaryLoading={summaryLoading}
            summaryError={summaryError}
            summary={summary}
            monthLabel={monthLabel}
            onRetry={() => void loadSummary()}
          />
        </section>

        <section className="alarm-content mb-2">
          <div className="toolbar-content pt-1 pb-1">
            <div className="toolbar-content__left">
              <h3 className="heading__title heading__title--activity">{headerTitle}</h3>
              <SearchQuery searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </div>

            <div className="toolbar-content__right">
              <div className="filter-container" style={{ position: "relative" }}>
                <div className="alarm-controls">
                  <FilterBtn
                    onClick={() => setIsFilterOpen(v => !v)}
                    isActive={statusFilter !== "all"}
                    isDropdownOpen={isFilterOpen}
                  />
                  <UserActivityLogFilter
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={activityTypeOptions}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className="heading__title heading__title--activity mb-1 mt-0">
            User Activity Logs ({Math.min(pageSize, filteredAndSorted.length)} of {filteredAndSorted.length})
          </h3>

          <ActivityContent
            loading={loading}
            error={error}
            pagedRows={pagedRows}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            handleSort={handleSort}
            searchQuery={searchQuery}
            onRetry={() => void loadActivity()}
          />
        </section>

        {!loading && !error && filteredAndSorted.length > pageSize && (
          <div className="mt-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={p => setCurrentPage(p)}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminUserActivity;
